// Screenshot Collector service (WHEEL-394). Contract source of truth is the
// live OpenAPI at https://api.bfore.ai/v2/screenshots/openapi.json - it is
// ahead of the RFC / tech spec in Confluence.

/**
 * Threat-lifecycle reason recorded against every stored capture. The API
 * stores stable snake_case codes (since 2026-09-22); the display wording is
 * the client's, see SCREENSHOT_STATUS_LABELS.
 */
export type ScreenshotStatus =
  | 'first_detected'
  | 'confirmed'
  | 'reconfirmed'
  | 'takedown_initiated'
  | 'takedown_completed'
  | 'manual_refresh';

/** Analyst-facing wording for each capture reason code. */
export const SCREENSHOT_STATUS_LABELS: Record<ScreenshotStatus, string> = {
  first_detected: 'Threat first detected',
  confirmed: 'Threat confirmed',
  reconfirmed: 'Threat re-confirmed',
  takedown_initiated: 'Takedown initiated',
  takedown_completed: 'Takedown completed',
  manual_refresh: 'Manual refresh',
};

/**
 * Labels the service stored before it switched to codes (2026-09-22).
 * Old rows still return them; normalise so they render like new rows.
 */
export const LEGACY_SCREENSHOT_STATUS_CODES: Record<string, ScreenshotStatus> =
  {
    'Threat first detected': 'first_detected',
    'Threat confirmed': 'confirmed',
    'Threat re-confirmed': 'reconfirmed',
    'Takedown initiated': 'takedown_initiated',
    'Takedown completed': 'takedown_completed',
    'Analyst manual refresh': 'manual_refresh',
  };

/** What one capture call did (distinct from ScreenshotStatus). */
export type ScreenshotCaptureOutcome = 'captured' | 'existing' | 'refreshed';

export interface ScreenshotTimelineEntry {
  /** Durable handle. `url` / `thumbnail_url` expire, this does not. */
  id: number;
  status: ScreenshotStatus | string;
  /** UTC capture time. Null only for legacy rows. */
  created: string | null;
  /** Signed, expiring URL for the full-size PNG. */
  url: string;
  /** Signed, expiring URL for the 320x180 WebP thumbnail. */
  thumbnail_url: string;
  /** UTC instant at which both signed URLs stop working. Null when the
   * storage backend issues non-expiring URLs (local dev). */
  sas_expires_at: string | null;
  capture_profile: string | null;
  /** Post-redirect URL at capture time. Null only for captures predating the
   * field - not a signal that the domain did not redirect. */
  final_url: string | null;
}

export interface ScreenshotTimelineResponse {
  total: number;
  limit: number;
  offset: number;
  items: ScreenshotTimelineEntry[];
}

/** Response of POST /refresh (and /collect, which Wheel never calls). */
export interface ScreenshotCaptureResponse {
  id: number;
  status: ScreenshotCaptureOutcome;
  url: string;
  thumbnail_url: string;
  sas_expires_at: string | null;
  created: string | null;
  capture_profile: string | null;
  final_url: string | null;
}
